import { fetchCompanyDetail, fetchNogaDetail } from "./dataFetching";

const EMPTY_ARRAY = [];

function extractActiveFilters(keyFiguresFilters, filterId, isInitialLoad) {
  if (isInitialLoad) return EMPTY_ARRAY;
  const filter = keyFiguresFilters?.filters?.find((item) => item.id === filterId);
  if (!filter) return EMPTY_ARRAY;
  return filter.items.filter((item) => item.active).map((item) => item.id);
}

export function extractRequestBodyFromFilters(keyFiguresFilters, isInitialLoad) {
  return {
    years: extractActiveFilters(keyFiguresFilters, "surveyYears", isInitialLoad),
    nogaCodes: extractActiveFilters(keyFiguresFilters, "nogaCode", isInitialLoad),
    officeSizeIds: extractActiveFilters(keyFiguresFilters, "officeSize", isInitialLoad),
    greaterRegions: extractActiveFilters(keyFiguresFilters, "greaterRegion", isInitialLoad),
  };
}

async function setInitialActiveNoga(filtersCopy, languageKey, nogaFilterIndex, companyAccess) {
  if (nogaFilterIndex < 0) return filtersCopy;
  const onError = (error) => console.error(error);
  const companyDetail = await fetchCompanyDetail(onError, companyAccess);
  const nogaId = companyDetail?.data?.[0]?.nogaId;
  if (nogaId == null) return activateDefaultNogaCode(filtersCopy, nogaFilterIndex);
  const nogaDetails = await fetchNogaDetail(languageKey, nogaId, onError);
  const nogaCode = nogaDetails?.data?.nogaCode;

  filtersCopy.filters[nogaFilterIndex].items = filtersCopy.filters[nogaFilterIndex].items.map((option) => ({
    ...option,
    active: String(option.id) === String(nogaCode),
  }));

  const noActiveNogas = filtersCopy.filters[nogaFilterIndex].items.every((item) => !item.active);

  if (noActiveNogas) {
    return activateDefaultNogaCode(filtersCopy, nogaFilterIndex);
  }

  return filtersCopy;
}

function setInitialActiveSurveyYear(filtersCopy) {
  const filterIndex = filtersCopy.filters.findIndex((filter) => filter.id === "surveyYears");

  if (filterIndex < 0) return filtersCopy;

  const items = filtersCopy.filters[filterIndex].items || [];
  const years = items.map((item) => Number(item.id)).filter(Number.isFinite);

  if (years.length === 0) return filtersCopy;

  const newestAvailableYear = Math.max(...years);

  filtersCopy.filters[filterIndex].items = items.map((item) => ({
    ...item,
    active: Number(item.id) === newestAvailableYear,
  }));

  return filtersCopy;
}

function activateDefaultNogaCode(filtersCopy, nogaFilterIndex) {
  if (nogaFilterIndex < 0) return filtersCopy;
  const ARCHI_OFFICE_NOGA_CODE = 711101;
  filtersCopy.filters[nogaFilterIndex].items = filtersCopy.filters[nogaFilterIndex].items.map((option) => ({
    ...option,
    active: String(option.id) === String(ARCHI_OFFICE_NOGA_CODE),
  }));
  return filtersCopy;
}

function updateFilterItems(filtersCopy, filter, submittedFilters, nextAvailableFilters) {
  const allItems = filtersCopy.allAvailabeFilters?.[filter.id] || [];
  const submittedIds = submittedFilters?.[filter.id] || [];
  if (filter.id === "surveyYears") {
    return allItems.map((year) => ({
      id: year,
      label: year,
      active: submittedIds.some((id) => String(id) === String(year)),
    }));
  }
  const availableIds = (nextAvailableFilters?.[filter.id] || []).map((item) => item.id);

  return allItems.map((item) => ({
    id: item.id,
    label: item.value,
    name: item.value,
    active: submittedIds.some((id) => String(id) === String(item.id)),
    disabled: !availableIds.some((id) => String(id) === String(item.id)),
  }));
}

export async function updateFilters(filters, submittedFilters, nextAvailableFilters, isInitialLoad, companyAccess, languageKey) {
  const filtersCopy = {
    ...filters,
    filters: (filters?.filters || []).map((filter) => ({
      ...filter,
      items: [...(filter.items || [])],
    })),
  };

  if (isInitialLoad) {
    filtersCopy.allAvailabeFilters = nextAvailableFilters || {};
  }

  filtersCopy.filters = filtersCopy.filters.map((filter) => {
    if (filter.id === "reference") return filter;

    return {
      ...filter,
      items: updateFilterItems(filtersCopy, filter, submittedFilters, nextAvailableFilters),
    };
  });

  if (!isInitialLoad) {
    return {
      ...filtersCopy,
      newest: false,
      isInitialLoad: false,
    };
  }

  const nogaFilterIndex = filtersCopy.filters.findIndex((filter) => filter.id === "nogaCode");

  if (companyAccess) {
    await setInitialActiveNoga(filtersCopy, languageKey, nogaFilterIndex, true);
  } else {
    activateDefaultNogaCode(filtersCopy, nogaFilterIndex);
  }

  setInitialActiveSurveyYear(filtersCopy);

  return {
    ...filtersCopy,
    newest: true,
    isInitialLoad: false,
  };
}

